import React, { memo } from 'react';
import { isNil } from 'lodash';
import classnames from 'classnames';

import { Button, EditableText, Switch, Tooltip } from '../../../../components';
import { copyIcon, trashIcon } from '../../rule/rule-utils';
import { MAX_TITLE_LENGTH } from '../consts';
import type { RateCartTermUpdater, TermGuid } from '../types';

interface RateCardTermHeaderProps {
	termGuid: TermGuid;
	titleText: string;
	className?: string;
	isEnabled?: boolean;
	isToggleDisabled?: boolean;
	copyTooltipText?: string;
	removeTooltipText?: string;
	switchTooltipText?: string;
	inactiveText?: string;
	readOnly?: boolean;
	updateTerm?(updater: Partial<RateCartTermUpdater>): void;
	onRemove?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
	onCopy?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
}

export const RateCardTermHeader = memo(
	({
		termGuid,
		titleText,
		isEnabled,
		updateTerm,
		className,
		onRemove,
		onCopy,
		copyTooltipText = 'Duplicate',
		removeTooltipText = 'Remove',
		switchTooltipText = 'Toggle Term Active',
		inactiveText = 'Inactive',
		isToggleDisabled = false,
		readOnly = false
	}: RateCardTermHeaderProps) => {
		const onTitleChange = updateTerm ? (term_title: string) => updateTerm({ term_title }) : undefined;
		const toggleIsEnabled = updateTerm
			? (active: boolean, e: React.MouseEvent) => {
					e.stopPropagation();
					updateTerm({ active });
			  }
			: undefined;

		return (
			<div className={classnames('term-header', className)} data-test-id={`term-header-${termGuid}`}>
				<div className="term-header-name">
					<EditableText
						value={titleText}
						onValueChange={onTitleChange}
						maxLength={MAX_TITLE_LENGTH}
						editAllowed={!readOnly}
						showEditButtonOnHover
					/>
					{!isEnabled && (
						<div className="inactive-badge">
							<span>{inactiveText}</span>
						</div>
					)}
				</div>
				<div className="term-header-buttons">
					{onCopy ? (
						<Tooltip title={copyTooltipText}>
							<Button
								shape="circle"
								className="copy-button"
								type="text"
								icon={copyIcon}
								onClick={onCopy}
								disabled={readOnly}
							/>
						</Tooltip>
					) : null}
					{onRemove ? (
						<Tooltip title={removeTooltipText}>
							<Button
								shape="circle"
								className="remove-button"
								icon={trashIcon}
								type="text"
								onClick={onRemove}
								disabled={readOnly}
							/>
						</Tooltip>
					) : null}
					{isNil(isEnabled) ? null : (
						<Tooltip title={switchTooltipText}>
							<Switch
								disabled={isToggleDisabled || readOnly}
								checked={isEnabled}
								onChange={toggleIsEnabled}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		);
	}
);
